/**
 * @license
 * @copyright Copyright Motili Inc., 2023 All Rights Reserved
 */

import axios from 'axios';
import { Checkbox, format, getMaskConfig, MaskType, SubText } from '@motili/ui';
import { ChangeEvent, ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { setStarterInfo } from '../../../common/redux/ducks/contractorRegistrationForm';
import { useAppDispatch } from '../../../common/redux/hooks';
import { StarterInfo } from '../../../common/types';
import { validatePhone } from '../../../common/utils';
import StyledMaskedTextInput from '../../../components/styled/StyledMaskedTextInput';
import StyledTextInput from '../../../components/styled/StyledTextInput';
import config from '../../../config';
import BottomButtonNavigation from '../../../navigation/BottomButtonNavigation';

interface GetStartedFormProps {
    starterInfo: StarterInfo;
}

export default function GetStartedForm({
    starterInfo,
}: GetStartedFormProps): ReactElement {
    const { firstName, lastName, email, phone, smsOptIn } = starterInfo;
    const dispatch = useAppDispatch();
    const {
        register,
        trigger,
        formState: { errors, isValid },
    } = useForm({
        mode: 'all',
        defaultValues: {
            firstName,
            lastName,
            email,
            phone: format(phone, getMaskConfig(MaskType.PHONE)),
            'sms-consent-checkbox': smsOptIn,
        },
    });

    const phoneReg = register('phone', {
        required: true,
        validate: validatePhone,
    });

    const smsConsentReg = register('sms-consent-checkbox', {
        required: true,
    });

    const navigate = useNavigate();

    const isPhoneValid = !!validatePhone(phone);

    const handleBackClick = () => navigate(`/contractor/welcome`);

    //trigger method checks to see if there are any errors in the form
    const handleNextClick = () => {
        trigger().then((isValid: boolean) => {
            if (isValid && isPhoneValid) {
                navigate(`/contractor/company`);
            }
        });
    };

    const handleInputChange = ({
        target: { name, value },
    }: ChangeEvent<HTMLInputElement>) => {
        dispatch(setStarterInfo({ ...starterInfo, [name]: value }));
    };

    const validateEmail = async (email: string) => {
        try {
            const { data: emailExists } = await axios.get(
                `${config.apiBaseUrl}/Accounts/accountEmailExists`,
                { params: { email } }
            );
            return emailExists
                ? 'This email address already exists in our system. Please contact Motili directly for assistance.'
                : true;
        } catch {
            return 'Unable to verify email. Please try again.';
        }
    };

    return (
        <>
            <form>
                <StyledTextInput
                    marginTop='20px'
                    withLabelSpacing
                    required
                    name={'firstName'}
                    error={!!errors.firstName}
                    errorMessage={'First Name cannot be blank'}
                    register={register('firstName', {
                        required: true,
                    })}
                    onChange={handleInputChange}
                    value={firstName}
                    style={inputStyles}
                    label='First Name'
                />
                <StyledTextInput
                    marginTop='20px'
                    withLabelSpacing
                    required
                    name={'lastName'}
                    error={!!errors.lastName}
                    errorMessage={'Last Name cannot be blank'}
                    register={register('lastName', {
                        required: true,
                    })}
                    onChange={handleInputChange}
                    value={lastName}
                    style={inputStyles}
                    label='Last Name'
                />
                <StyledTextInput
                    marginTop='20px'
                    withLabelSpacing
                    required
                    name={'email'}
                    type='email'
                    error={!!errors.email}
                    errorMessage={(errors?.email?.message as string) || ''}
                    register={register('email', {
                        required: 'Please provide a valid email address',
                        pattern: {
                            value: /\S+@\S+\.\S+/,
                            message:
                                'Entered value does not match email format',
                        },
                        validate: validateEmail,
                    })}
                    onChange={handleInputChange}
                    value={email}
                    style={inputStyles}
                    label='Email Address'
                />
                <SubText>
                    This will be used as your username and cannot be edited
                    later.
                </SubText>
                <StyledMaskedTextInput
                    marginTop='20px'
                    withLabelSpacing
                    required
                    name={'phone'}
                    error={!!errors.phone}
                    errorMessage={'Please enter a valid phone'}
                    style={inputStyles}
                    register={phoneReg}
                    onChange={e =>
                        dispatch(
                            setStarterInfo({
                                ...starterInfo,
                                phone: e.target.value,
                            })
                        )
                    }
                    onBlur={phoneReg.onBlur}
                    value={phone}
                    label='Phone Number'
                    mask={MaskType.PHONE}
                    type='tel'
                    saveUnmasked
                />
                <br />
                <Checkbox
                    label={
                        'I agree to receive SMS messages from Motili related to work orders, scheduling, and account communications. Msg & data rates may apply. Msg frequency varies.'
                    }
                    id={'sms-consent-checkbox'}
                    value={''}
                    onChange={e => {
                        smsConsentReg.onChange(e);
                        dispatch(
                            setStarterInfo({
                                ...starterInfo,
                                smsOptIn: !smsOptIn,
                            })
                        );
                    }}
                    name={'sms-consent-checkbox'}
                    checked={smsOptIn}
                    required={true}
                    register={smsConsentReg}
                />
            </form>
            <BottomButtonNavigation
                onBackClick={handleBackClick}
                onNextClick={handleNextClick}
                nextDisabled={!isValid || !isPhoneValid}
            />
        </>
    );
}

const inputStyles = { width: '100%' };
